import type { CreatorPlatform } from '../shared/platforms';
import type {
	AnalyticsPeriod,
	CommissionStatus,
	Lob,
	PayoutDestination,
	PlatformStatus,
	WithdrawalStatus,
} from './types';

// User-facing text for the creator dashboard, grouped by screen. Amounts and
// dates arrive already formatted ("₹1,240", "2 Oct").

export const NAV_TEXT = {
	pageTitle: 'Creator dashboard · ixigo Creators',
	homeLink: 'Creator dashboard home',
	sidebarLabel: 'Creator dashboard',
	tabsLabel: 'Dashboard',
	yourAccount: 'Your account',
	tabs: {
		home: 'Home',
		social: 'Social',
		itinerary: 'Itinerary',
		analytics: 'Analytics',
		payout: 'Payout',
	},
} as const;

/** Tabs that aren't live yet. */
export const PLACEHOLDER_TEXT = {
	onItsWay: 'This tab is on its way.',
	itineraryTitle: 'Itineraries',
} as const;

export const LOAD_ERROR_TEXT = {
	title: 'Something went wrong',
	body: (what: string) => `We couldn’t load your ${what}. Please try again.`,
	dashboard: 'dashboard',
	payouts: 'payouts',
	analytics: 'analytics',
	platforms: 'platforms',
} as const;

export const LOB_LABELS: Record<Lob, string> = {
	flights: 'Flights',
	hotels: 'Hotels',
};

export const HOME_TEXT = {
	title: 'Home',
	codeLabel: 'Your creator code',
	live: 'Live',
	liveSince: (date: string) => `Live since ${date}`,
	copyCode: 'Copy code',
	copied: 'Copied',
	isLive: 'is live.',
	followersGet: (percent: number) => `Your followers get ${percent}% off`,
	discountCap: (lob: string, amount: string) => `${lob} up to ${amount}`,
	lifetimeTitle: 'Lifetime earnings',
	noBookings: 'No bookings with your code yet',
	fromBookings: (bookings: string) => `from ${bookings} with your code`,
	confirmedTitle: 'Confirmed earnings',
	notWithdrawn: 'Not withdrawn yet',
	confirmed: 'Confirmed',
	confirmedNote: 'trip completed',
	pending: 'Pending',
	pendingNote: 'trip coming up',
	toPayout: (amount: string, isFirst: boolean) =>
		`${amount} more to your ${isFirst ? 'first' : 'next'} payout`,
	readyToWithdraw: (amount: string) => `${amount} ready to withdraw`,
	ratesTitle: 'What you earn per booking',
	rateCap: (amount: string) => `up to ${amount} a booking`,
	cancelledNote: 'Cancelled bookings drop out of pending.',
} as const;

/** Shared by Home and Payout. */
export const PAYOUT_MINIMUM_TEXT = (amount: string) =>
	`Withdrawals open at ${amount} confirmed earnings.`;

export const ANALYTICS_PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
	'30d': 'Last 30 days',
	'60d': 'Last 60 days',
	'90d': 'Last 90 days',
	'6m': 'Last 6 months',
	'1y': 'Last 1 year',
};

export const ANALYTICS_TEXT = {
	title: 'Analytics',
	periodLabel: 'Period',
	customRange: 'Custom range',
	/** How far back a custom range can go. */
	customRangeLimit: (earliest: string) =>
		`Any dates from ${earliest} up to today.`,
	from: 'From',
	to: 'To',
	notPicked: 'Select',
	pickStart: 'Pick a start date',
	pickEnd: 'Now pick an end date',
	days: (count: number) => `${count} ${count === 1 ? 'day' : 'days'} selected`,
	apply: 'Apply',
	previousMonths: 'Earlier months',
	nextMonths: 'Later months',
	allBookings: 'All',
	earnings: 'Earnings',
	earningsNote: 'excludes cancellations',
	conversion: 'Conversion',
	conversionNote: 'booked ÷ code applied',
	funnelTitle: 'From code applied to booking',
	codeApplied: 'Code applied',
	noApplications: 'No one has applied your code yet',
	wentOnToBook: (share: string) => `${share} went on to book`,
	bookings: 'Bookings',
	weeklyTitle: 'Earnings by week',
	weekOf: 'Week of ',
	bookingsTitle: 'Your bookings',
	averageOrderValue: 'Avg. order value',
	couponsUsed: 'Coupons used',
} as const;

export const PAYOUT_TEXT = {
	title: 'Payout',
	readyToWithdraw: 'Ready to withdraw',
	onHold: 'Payout pending verification',
	minimumReached: (amount: string) => `${amount} minimum reached`,
	toGo: (amount: string) => `${amount} to go`,
	withdraw: 'Withdraw',
	/** "your Telegram channel", "your Telegram and WhatsApp channels". */
	yourChannels: (platforms: string, count: number) =>
		`your ${platforms} ${count === 1 ? 'channel' : 'channels'}`,
	yourChannel: 'your channel',
	opensOnceVerified: (channels: string, count: number) =>
		`Opens once ${channels} ${count > 1 ? 'are' : 'is'} verified.`,
	wouldGet: (amount: string) => `At ${amount} you would get`,
	bonus: (percent: number) => `+${percent}%`,
	earningsByStatus: 'Earnings by status',
	pending: 'Pending',
	confirmed: 'Confirmed',
	paidOut: 'Paid out',
} as const;

/** ixigo money pays a bonus on top; ixigo money max can be moved to a bank
 *  account and is paid at face value. */
export const DESTINATION_TEXT: Record<
	PayoutDestination,
	{
		readonly name: string;
		/** Short line for the Payout tab's preview. */
		readonly where: string;
		/** Longer line for the Withdraw sheet. */
		readonly description: string;
	}
> = {
	ixigo_money: {
		name: 'ixigo money',
		where: 'ixigo wallet',
		description: 'Goes to your ixigo wallet. Use it on flights and hotels.',
	},
	ixigo_money_max: {
		name: 'ixigo money max',
		where: 'transfer to your bank',
		description: 'Transfer it to your bank account. Paid at face value.',
	},
};

/** Shared by the Withdraw and custom range sheets and dialogs. */
export const OVERLAY_TEXT = {
	close: 'Close',
	cancel: 'Cancel',
} as const;

export const WITHDRAW_TEXT = {
	title: (amount: string) => `Withdraw ${amount}`,
	description: 'Choose where your earnings go.',
	destinationsLabel: 'Payout destination',
	bonus: (percent: number) => `+${percent}% bonus`,
	termsLink: 'ixigo money terms: expiry and usage limits',
	submit: (amount: string, destination: string) =>
		`Withdraw ${amount} to ${destination}`,
	sent: (amount: string, destination: string) =>
		`${amount} sent to ${destination}`,
	processing: (amount: string, destination: string) =>
		`Your ${amount} withdrawal to ${destination} is processing`,
	/** The payout came back FAILED: nothing was credited. */
	failed:
		"Your withdrawal didn't go through. Your balance hasn't changed, so you can try again.",
	/** No answer, or a server error: it may or may not have gone through. */
	unanswered: "Couldn't withdraw right now. Please try again.",
} as const;

export const PREVIOUS_PAYOUTS_TEXT = {
	title: 'Previous payouts',
	seeAll: 'See all',
	/** "2 Oct", "2 Oct · ₹1,200 + ₹120 bonus", "9 Sep · not credited". */
	describe: (date: string, detail: string | null) =>
		detail ? `${date} · ${detail}` : date,
	withBonus: (withdrawn: string, bonus: string) =>
		`${withdrawn} + ${bonus} bonus`,
	notCredited: 'not credited',
	columns: ['Date', 'Paid to', 'Withdrawn', 'Bonus', 'Status'],
	creditedColumn: 'Credited',
	bonus: (amount: string) => `+${amount}`,
	noBonus: '–',
} as const;

export const PAYOUT_STATUS_LABELS: Record<WithdrawalStatus, string> = {
	success: 'Credited',
	processing: 'Processing',
	failed: 'Failed',
};

export const HISTORY_TEXT = {
	title: 'History',
	seeAll: 'See all',
	showMore: 'Show more',
	empty: 'No bookings with your code yet.',
	loadFailed: "Couldn't load your history. Please try again.",
	columns: ['Booking', 'Type', 'Travel', 'Status'],
	commissionColumn: 'Commission',
	cancelled: 'Cancelled',
	/** A booking's title while its details are still syncing. */
	syncingTitle: { flights: 'Flight booking', hotels: 'Hotel stay' },
	/** "Flight · travels 18 Oct", "Hotel · booking cancelled". */
	describe: (type: string, detail: string) => `${type} · ${detail}`,
	bookingCancelled: 'booking cancelled',
	datesToFollow: 'dates to follow',
	/** Upcoming and past, for each booking type. */
	travel: {
		flights: { upcoming: 'travels', done: 'travelled' },
		hotels: { upcoming: 'stays', done: 'stayed' },
	},
} as const;

/** Singular, for a History row: "Flight", "Hotel". */
export const BOOKING_TYPE_LABELS: Record<Lob, string> = {
	flights: 'Flight',
	hotels: 'Hotel',
};

export const COMMISSION_STATUS_LABELS: Record<CommissionStatus, string> = {
	pending: 'Pending',
	confirmed: 'Confirmed',
	paid: 'Paid',
	reversed: 'Reversed',
};

export const VERIFICATION_TEXT = {
	label: 'Channel verification',
	progressLabel: 'Verification progress',
	/** Read before each step, for screen readers. */
	stepStates: { done: 'Done', current: 'Now', next: 'Next' },
	codeLive: 'Code live, earning',
	since: (date: string) => `Since ${date}`,
	teamChecks: (count: number) =>
		count > 1 ? 'Our team checks the channels' : 'Our team checks the channel',
	inProgress: 'In progress',
	withdrawalsOpen: 'Withdrawals open',
	earningsKept: "Everything you've earned is kept",
} as const;

/** What each platform calls its audience. */
export const AUDIENCE_UNITS: Record<CreatorPlatform, string> = {
	instagram: 'followers',
	youtube: 'subscribers',
	x: 'followers',
	snapchat: 'followers',
	whatsapp: 'members',
	telegram: 'members',
};

export const SOCIAL_TEXT = {
	title: 'Social',
	intro: 'Where your code is shared, and whether each platform is verified.',
	connectedLabel: 'Connected platforms',
	approvedOn: (date: string) => `Approved ${date}`,
	addedOn: (date: string) => `Added ${date}`,
	awaitingVerificationNote:
		'Your code already works here. Payouts open once our team verifies this channel.',
	addingSoon: "You'll be able to add more platforms here soon.",
} as const;

export const PLATFORM_STATUS_LABELS: Record<PlatformStatus, string> = {
	verified: 'Verified',
	verification_pending: 'Verifying',
	in_review: 'In review',
	rejected: 'Not approved',
};
